'use client';

import { useEffect, useState } from 'react';

import NextImage from 'next/image';

import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../helpers/analytics/trackEvent';
import { useClientHasRendered } from '../../../helpers/hooks';
import { Button } from '../../Clickables/Buttons/Button';
import { Container } from '../../Container/Container';
import { Heading } from '../../Heading/Heading';

import { twMerge } from 'tailwind-merge';
import { useIsScrollingDown } from '../../../helpers/hooks/useIsScrollingDown';
import activeLinkIcon from './assets/activeLinkIcon.svg';
import { useIsTouchDevice } from '../../../helpers/hooks/useIsTouchDevice';

export interface PageNavigationItem {
  id: string;
  label: string;
  orderMobile?: number;
  orderTablet?: number;
  orderDesktop?: number;
}
interface IProps {
  navItems: PageNavigationItem[];
  className?: string;
  containerClassName?: string;
}

const getActiveId = (navItems: IProps['navItems']): string => {
  const lastOnscreenItem = [...navItems].reverse().find(({ id }) => {
    // This returns the last item in the list that is above the halfway point of the screen height.
    return (document.getElementById(id)?.getBoundingClientRect().top ?? 0) < window.innerHeight / 2;
  });

  if (window.scrollY === 0) {
    return navItems[0].id;
  }

  if (lastOnscreenItem) {
    return lastOnscreenItem.id;
  }

  return navItems[navItems.length - 1].id;
};

export const PageNavigation = ({ navItems, className, containerClassName }: IProps) => {
  const [activeId, setActiveId] = useState(navItems.length ? navItems[0].id : undefined);
  const [ignoreScroll, setIgnoreScroll] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const hasClientRendered = useClientHasRendered();
  const { currentPage } = useAnalytics();
  const isScrollingDown = useIsScrollingDown();
  const isTouchDevice = useIsTouchDevice();

  useEffect(() => {
    const onScroll = (): void => {
      if (!ignoreScroll) {
        setActiveId(getActiveId(navItems));
      }

      if (isScrollingDown) {
        setMenuVisible(true);
      }
    };
    const onScrollEnd = (): void => {
      setIgnoreScroll(false);
    };
    // Delays in the CSS render can cause the activeId calculation to be off, so this ensures we
    // wait until things have rendered
    if (hasClientRendered) {
      document.addEventListener('scroll', onScroll);
      document.addEventListener('scrollend', onScrollEnd);
    }

    return () => {
      document.removeEventListener('scroll', onScroll);
      document.removeEventListener('scrollEnd', onScrollEnd);
    };
  }, [navItems, hasClientRendered, isScrollingDown, ignoreScroll]);

  const onMenuItemClicked = (id: string): void => {
    const element = document.getElementById(id);
    if (element) {
      setIgnoreScroll(true);
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }

    void trackEvent({
      name: 'mykiss_navigation_clicked',
      properties: { destination: id, source: currentPage, level: 'l2' },
    });
  };

  return (
    <nav
      className={twMerge(
        `${menuVisible ? 'block' : 'hidden'} z-nav sm:z-sub-nav sticky top-0 min-h-(--main-navigation-height-mobile) bg-white shadow-sm transition-all sm:top-(--main-navigation-height) sm:block`,
        className,
      )}
      data-testid="PageNavigation"
    >
      <Container
        className={twMerge(
          `flex h-full flex-row items-center px-0 sm:justify-center sm:p-0 md:items-end md:justify-end`,
          containerClassName,
        )}
      >
        <div
          className={`flex ${isTouchDevice ? 'scrollbar-none' : ''} min-h-(--main-navigation-height-mobile) flex-row gap-5 overflow-auto px-4 whitespace-nowrap sm:gap-6 md:px-0`}
        >
          {navItems.map(({ id, label, orderMobile, orderTablet, orderDesktop }) => (
            <Button
              key={`sub-nav2-${id}`}
              variant="textNone"
              className={`relative flex ${orderMobile ? `order-${orderMobile}` : ''} ${orderTablet ? `sm:order-${orderTablet}` : ''} ${orderDesktop ? `md:order-${orderDesktop}` : ''} items-center justify-center`}
              onClick={() => {
                onMenuItemClicked(id);
              }}
              data-testid="PageNavigation Item"
            >
              <Heading
                as="span"
                withDefaults={false}
                variant="3xs"
                color={activeId === id ? 'navHighlight' : 'dark'}
                className="font-heading hover:text-pinktail-shade flex flex-col items-center"
              >
                {label}
                <NextImage
                  alt="Active Link Icon"
                  width={21}
                  height={6}
                  className={`absolute bottom-0 mt-2 ${activeId === id ? 'block' : 'hidden'}`}
                  src={activeLinkIcon}
                />
              </Heading>
            </Button>
          ))}
        </div>
      </Container>
    </nav>
  );
};
